import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Sparkles, Eye, EyeOff } from 'lucide-react';

export default function Register() {
    const navigate = useNavigate();
    const { register } = useAuth();
    const [loading, setLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        password: '',
        confirmPassword: '',
    });

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (formData.password !== formData.confirmPassword) {
            toast.error('Les mots de passe ne correspondent pas');
            return;
        }

        if (formData.password.length < 6) {
            toast.error('Le mot de passe doit contenir au moins 6 caractères');
            return;
        }

        setLoading(true);

        try {
            await register(formData.name, formData.email, formData.password);
            toast.success('Compte créé avec succès');
            navigate('/');
        } catch (error) {
            const message = error.response?.data?.detail || "Erreur lors de l'inscription";
            toast.error(message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="auth-split">
            {/* Form Side */}
            <div className="auth-form-side bg-background">
                <div className="w-full max-w-md space-y-8">
                    <div className="text-center">
                        <div className="flex justify-center mb-4">
                            <img src="/bbm-logo.png" alt="BBM" className="w-20 h-20 rounded-[2px] object-cover" />
                        </div>
                        <h1 className="text-3xl font-semibold tracking-tight">Créer un compte</h1>
                        <p className="text-muted-foreground mt-2">
                            Rejoignez BBM pour gérer vos commandes
                        </p>
                    </div>

                    <form onSubmit={handleSubmit} className="space-y-5" data-testid="register-form">
                        <div className="space-y-2">
                            <Label htmlFor="name">Nom complet</Label>
                            <Input
                                id="name"
                                type="text"
                                placeholder="Votre nom"
                                value={formData.name}
                                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                className="input-luxury"
                                required
                                data-testid="name-input"
                            />
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="email">Email</Label>
                            <Input
                                id="email"
                                type="email"
                                placeholder="votre@email.com"
                                value={formData.email}
                                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                className="input-luxury"
                                required
                                data-testid="email-input"
                            />
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="password">Mot de passe</Label>
                            <div className="relative">
                                <Input
                                    id="password"
                                    type={showPassword ? 'text' : 'password'}
                                    placeholder="••••••••"
                                    value={formData.password}
                                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                                    className="input-luxury pr-10"
                                    required
                                    data-testid="password-input"
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPassword(!showPassword)}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                                >
                                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                </button>
                            </div>
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="confirmPassword">Confirmer le mot de passe</Label>
                            <Input
                                id="confirmPassword"
                                type="password"
                                placeholder="••••••••"
                                value={formData.confirmPassword}
                                onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                                className="input-luxury"
                                required
                                data-testid="confirm-password-input"
                            />
                        </div>

                        <Button
                            type="submit"
                            className="w-full btn-luxury"
                            disabled={loading}
                            data-testid="register-submit-btn"
                        >
                            {loading ? (
                                <span className="flex items-center gap-2">
                                    <span className="spinner" />
                                    Création...
                                </span>
                            ) : (
                                'Créer le Compte'
                            )}
                        </Button>
                    </form>

                    <p className="text-center text-sm text-muted-foreground">
                        Déjà un compte ?{' '}
                        <Link
                            to="/connexion"
                            className="text-primary hover:underline font-medium"
                            data-testid="login-link"
                        >
                            Se connecter
                        </Link>
                    </p>
                </div>
            </div>

            {/* Image Side */}
            <div
                className="auth-image-side"
                style={{
                    backgroundImage: 'url(https://images.unsplash.com/photo-1594994043623-ef50cf163197?w=1200)',
                }}
            >
                <div className="auth-image-overlay">
                    <blockquote className="text-white">
                        <p className="text-2xl font-light italic mb-4">
                            "Un parfum, c'est un bijou invisible."
                        </p>
                        <footer className="text-white/70 text-sm">
                            — Coco Chanel
                        </footer>
                    </blockquote>
                </div>
            </div>
        </div>
    );
}
